// creator-portal/src/context/AuthContext.tsx
// ✅ UPDATED: Added ownerId and country support to match iOS

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService, UserProfile } from '../services/api';

// ✅ Registration response interface
interface RegisterResult {
  success: boolean;
  verificationRequired: boolean;
  message?: string;
  user?: UserProfile;
}

interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    ownerId?: string,    // ✅ NEW: Creator Key
    country?: string     // ✅ NEW: Country code
  ) => Promise<RegisterResult>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProfile = async () => {
      if (apiService.isAuthenticated()) {
        try {
          const profile = await apiService.getProfile();
          setUser(profile);
        } catch {
          apiService.clearTokens();
        }
      }
      setLoading(false);
    };
    loadProfile();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await apiService.login({ email, password });
    setUser(response.data.user);
  };

  // ✅ UPDATED: Added ownerId and country parameters
  const register = async (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    ownerId?: string,    // ✅ NEW
    country?: string     // ✅ NEW
  ): Promise<RegisterResult> => {
    const response = await apiService.register({
      email,
      password,
      firstName,
      lastName,
      ownerId,   // ✅ Pass to API
      country,   // ✅ Pass to API
    });

    // Check if registration was successful
    if (response.success) {
      // Check if email verification is required (normal flow)
      if (response.data?.verificationRequired) {
        // Don't set user or try to login - user needs to verify email first
        return {
          success: true,
          verificationRequired: true,
          message:
            response.message || 'Registration successful. Please check your email to verify your account.',
          user: response.data?.user,
        };
      }

      // In case backend returns without verification required
      if (response.data?.user) {
        return {
          success: true,
          verificationRequired: false,
          message: response.message || 'Registration successful.',
          user: response.data.user,
        };
      }
    }

    // Registration failed - throw error to be caught by caller
    throw new Error(response.message || response.error || 'Registration failed');
  };

  const logout = async () => {
    await apiService.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};